/**
 * API Configuration
 * 
 * This file centralizes all API endpoint configurations.
 * Environment variables are loaded from .env file.
 * 
 * For Vite: Use import.meta.env.VITE_*
 * For Create React App: Use process.env.REACT_APP_*
 */

// Detect if we're using Vite or Create React App
const isVite = typeof import.meta !== 'undefined' && import.meta.env;

// Get environment variables (works with both Vite and CRA)
const getEnvVar = (key) => {
  if (isVite) {
    // For Vite: try the key as-is, then try with VITE_ prefix
    return import.meta.env[key] || import.meta.env[`VITE_${key.replace('REACT_APP_', '').replace('VITE_', '')}`];
  } else {
    // For Create React App: try REACT_APP_ prefix, then try as-is
    return process.env[`REACT_APP_${key.replace('VITE_', '').replace('REACT_APP_', '')}`] || process.env[key];
  }
};

// API Base Configuration
// Directly read from environment variables (Vite uses import.meta.env, CRA uses process.env)
let API_BASE_URL, API_BASE_PATH, UPLOADS_BASE_URL, SIGNALR_HUB_URL;

if (isVite) {
  // Vite environment variables (must start with VITE_)
  API_BASE_URL = import.meta.env.REACT_APP_API_BASE_URL || 'http://localhost/AlumniAPI';
  API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api';
  UPLOADS_BASE_URL = import.meta.env.VITE_UPLOADS_BASE_URL || 'https://localhost:44378/Uploads';
  SIGNALR_HUB_URL = import.meta.env.VITE_SIGNALR_HUB_URL || 'http://localhost/AlumniAPI';
  
  // Debug logging (only in development)
  if (import.meta.env.DEV) {
    // console.log('[API Config] Using Vite environment variables:');
    // console.log('  VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL || '(not set, using default)');
    // console.log('  VITE_API_BASE_PATH:', import.meta.env.VITE_API_BASE_PATH || '(not set, using default)');
  }
} else {
  // Create React App environment variables (must start with REACT_APP_)
  API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.impreserp.co.in/alumni.jjcet';
  API_BASE_PATH = process.env.REACT_APP_API_BASE_PATH || '/api';
  UPLOADS_BASE_URL = process.env.REACT_APP_UPLOADS_BASE_URL || 'https://api.impreserp.co.in/alumni.jjcet/Uploads';
  SIGNALR_HUB_URL = process.env.REACT_APP_SIGNALR_HUB_URL || 'https://api.impreserp.co.in/alumni.jjcet/chathub';
  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    // console.log('[API Config] Using Create React App environment variables:');
    // console.log('  REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL || '(not set, using default)');
    // console.log('  REACT_APP_API_BASE_PATH:', process.env.REACT_APP_API_BASE_PATH || '(not set, using default)');
  }
}

// Full API Base URL
const API_URL = `${API_BASE_URL}${API_BASE_PATH}`;

// Debug: Log the final API URL (only in development)
if ((isVite && import.meta.env.DEV) || (!isVite && process.env.NODE_ENV === 'development')) {
  // console.log('[API Config] Final API_URL:', API_URL);
  // console.log('[API Config] Example endpoint:', `${API_URL}/Auth/CheckUser`);
}

/**
 * API Endpoints Configuration
 */
const API_ENDPOINTS = {
  // Auth Endpoints
  AUTH: {
    CHECK_USER: `${API_URL}/Auth/CheckUser`,
    CHECK_STAFF_BY_LOGIN_CODE: `${API_URL}/Auth/CheckStaffByLoginCode`,
    LOGIN: `${API_URL}/Auth/login`,
    REGISTER: `${API_URL}/Auth/register`,
  },

  // Profile Endpoints
  PROFILE: {
    BASE: `${API_URL}/AlunmusProfile`,
    
    // Basic Info
    GET_BASIC_INFO: (studentId) => `${API_URL}/AlunmusProfile/basic/${studentId}`,
    UPDATE_BASIC_INFO: `${API_URL}/AlunmusProfile/update-basic-info`,
    
    // Statutory Info
    GET_STATUTORY_INFO: (studentId) => `${API_URL}/AlunmusProfile/StatutoryInfo/${studentId}`,
    UPDATE_STATUTORY_INFO: `${API_URL}/AlunmusProfile/update-statutory-info`,
    
    // Address Info
    GET_ADDRESS_INFO: (studentId) => `${API_URL}/AlunmusProfile/AddressInfo/${studentId}`,
    UPDATE_ADDRESS_INFO: `${API_URL}/AlunmusProfile/update-address-info`,
    GET_LOCATIONS_BY_DISTRICT: (districtId) => `${API_URL}/AlunmusProfile/GetLocationsOrTaluksInDistrictAsync?district=${districtId}`,
    
    // Family Info
    GET_FAMILY_MEMBERS: (studentId) => `${API_URL}/AlunmusProfile/FamilyMembersInfo/${studentId}`,
    UPDATE_FATHER_INFO: `${API_URL}/AlunmusProfile/update-father-info`,
    UPDATE_MOTHER_INFO: `${API_URL}/AlunmusProfile/update-mother-info`,
    UPDATE_SPOUSE_INFO: `${API_URL}/AlunmusProfile/update-spouse-info`,
    UPDATE_GUARDIAN_INFO: `${API_URL}/AlunmusProfile/update-guardian-info`,
    
    // Education Details V2
    GET_EDUCATION_DETAILS_V2: (studentId) => `${API_URL}/AlunmusProfile/EducationDetailsV2/${studentId}`,
    INSERT_EDUCATION_DETAIL_V2: `${API_URL}/AlunmusProfile/InsertEducationDetailV2`,
    UPDATE_EDUCATION_DETAIL_V2: `${API_URL}/AlunmusProfile/UpdateEducationDetailV2`,
    DELETE_EDUCATION_DETAIL_V2: (id, studentId) => `${API_URL}/AlunmusProfile/DeleteEducationDetailV2/${id}/${studentId}`,
    GET_INSTITUTES_BY_TYPE: (instituteType, district) => `${API_URL}/AlunmusProfile/GetInstitutesByType?instituteType=${instituteType}&district=${district}`,
    INSERT_INSTITUTE: `${API_URL}/AlunmusProfile/InsertInstitutesPrevMinimalAsync`,
    
    // Working Details V2
    GET_WORKING_DETAILS_V2: (studentId) => `${API_URL}/AlunmusProfile/WorkingDetailsV2/${studentId}`,
    INSERT_WORKING_DETAIL_V2: `${API_URL}/AlunmusProfile/InsertWorkingDetailV2`,
    UPDATE_WORKING_DETAIL_V2: `${API_URL}/AlunmusProfile/UpdateWorkingDetailV2`,
    DELETE_WORKING_DETAIL_V2: (id, studentId) => `${API_URL}/AlunmusProfile/DeleteWorkingDetailV2/${id}/${studentId}`,
    
    // Social Media Links
    GET_SOCIAL_MEDIA_LINKS: (studentId) => `${API_URL}/AlunmusProfile/FetchStudentSocialMediaLinksAsync?studentId=${studentId}`,
    MERGE_SOCIAL_MEDIA_LINK: `${API_URL}/AlunmusProfile/MergeStudentSocialMediaLinkAsync`,
    
    // Dropdowns
    GET_DROPDOWNS: `${API_URL}/AlunmusProfile/ManageDropDowns`,
    
    // Profile Settings
    GET_PROFILE_PRIVACY_SETTINGS: (studentId) => `${API_URL}/AlunmusProfile/FetchProfilePrivacySettings/${studentId}`,
    UPDATE_PROFILE_PRIVACY_SETTINGS: `${API_URL}/AlunmusProfile/MergeProfilePrivacySettings`,
    
    // Profile Images
    GET_STUDENT_IMAGE: (studentId) => `${API_URL}/AlunmusProfile/GetStudentImageAsync?StudentId=${studentId}`,
    MERGE_STUDENT_IMAGE: `${API_URL}/AlunmusProfile/MergeStudentImageAsync`,
    
    // Quick Profile
    GET_QUICK_PROFILE: (studentId) => `${API_URL}/AlunmusProfile/FetchQuickProfile/${studentId}`,
    UPDATE_QUICK_PROFILE: `${API_URL}/AlunmusProfile/QuickUpdateStudentBasicInfo`,
    
    // Full Profile
    GET_FULL_PROFILE: (studentId) => `${API_URL}/AlunmusProfile/FetchFullProfile/${studentId}`,
    
    // Expertise
    GET_STUDENT_EXPERTISE: (studentId) => `${API_URL}/AlunmusProfile/FetchStudentExpertise/${studentId}`,
    GET_MASTER_EXPERTISE: `${API_URL}/AlunmusProfile/FetchMasterExpertise`,
    MERGE_STUDENT_EXPERTISE: `${API_URL}/AlunmusProfile/MergeStudentExpertise`,
    MERGE_MASTER_EXPERTISE: `${API_URL}/AlunmusProfile/MergeMasterExpertise`,
    
    // District Summary
    GET_DISTRICT_SUMMARY: `${API_URL}/AlunmusProfile/GetDistrictStudentSummaryGPSAsync`,
    
    // Chapters
    FETCH_CHAPTERS: (chapterId, studentId) => `${API_URL}/AlunmusProfile/FetchChaptersAsync?chapterId=${chapterId}&studentId=${studentId}`,
    FETCH_CHAPTER_MEMBERS: (chapterId) => `${API_URL}/AlunmusProfile/FetchChapterMembers?chapterId=${chapterId}`,
    MERGE_CHAPTER_MEMBER: `${API_URL}/AlunmusProfile/MergeChapterMemberAsync`,
    DELETE_CHAPTER_MEMBER: `${API_URL}/AlunmusProfile/DeleteChapterMember`,
    GET_STUDENT_PROFILE: (studentId) => `${API_URL}/AlunmusProfile/GetStudentProfile?StudentId=${studentId}`,
    
    // Events
    FETCH_CHAPTER_EVENTS_WITH_DETAILS: (studentId) => `${API_URL}/AlunmusProfile/FetchChapterEventsWithDetails?StudentId=${studentId}`,
    FETCH_EVENTS_MEMBERS: `${API_URL}/AlunmusProfile/FetchEventsMembers`,
    MERGE_EVENTS_MEMBER: `${API_URL}/AlunmusProfile/MergeEventsMember`,
    
    // Careers
    GET_CAREER_RECORDS: `${API_URL}/AlunmusProfile/GetCareerRecords`,
    MERGE_CAREER_RECORD: `${API_URL}/AlunmusProfile/MergeCareerRecord`,
    GET_CAREER_COMMENTS_WITH_REPLIES: (careerId) => `${API_URL}/AlunmusProfile/GetCareerCommentsWithReplies/${careerId}`,
    ADD_CAREER_COMMENT: `${API_URL}/AlunmusProfile/AddCareerComment`,
    MERGE_CAREER_COMMENT_REPLY: `${API_URL}/AlunmusProfile/MergeCareerCommentReply`,
    
    // Children Details
    GET_CHILDREN_INFO: (studentId) => `${API_URL}/AlunmusProfile/ChildrenInfo/${studentId}`,
    MERGE_CHILDREN_INFO: `${API_URL}/AlunmusProfile/MergeChildran-info`,
    
    // Profile Description
    GET_PROFILE_DESCRIPTION: (studentId) => `${API_URL}/AlunmusProfile/FetchProfileDescription/${studentId}`,
    UPDATE_PROFILE_DESCRIPTION: `${API_URL}/AlunmusProfile/UpdateProfileDescription`,
  },

  // Admin Endpoints
  ADMIN: {
    BASE: `${API_URL}/AdminMaster`,
    GET_ALUMNUS_DETAILS: `${API_URL}/AdminMaster/get-alumnus-details`,
    GET_ALUMNUS_DETAIL_INVITATION: `${API_URL}/AdminMaster/get-alumnus-detail-invitation`,
    GET_ALUMNUS_FULL_DETAILS_BATCHWISE: `${API_URL}/AdminMaster/get-alumus-full-details-batchwise`,
    GET_ALUMNI_REPORTS_WITH_FILTERS: `${API_URL}/AdminMaster/get-alumni-reports-with-filters`,
    GET_FILTER_DROPDOWN_OPTIONS: `${API_URL}/AdminMaster/get-filter-dropdown-options`,
    UPDATE_ALUMNI_DETAILS: `${API_URL}/AdminMaster/update-alumni-details`,
    INSERT_ALUMNI_DETAILS: `${API_URL}/AdminMaster/insert-alumni-details`,
    GET_ALUMNUS_DASHBOARD_DATA: `${API_URL}/AdminMaster/GetAlumnusDashboardData`,
    GET_ALUMNI_STATISTICS: `${API_URL}/AdminMaster/GetAlumniStatistics`,
    GET_CHAPTER_EVENT_STATISTICS: `${API_URL}/AdminMaster/GetChapterEventStatistics`,
    GET_CHAPTER_ANALYTICS: `${API_URL}/AdminMaster/GetChapterAnalytics`,
    GET_EVENT_ANALYTICS: `${API_URL}/AdminMaster/GetEventAnalytics`,
    GET_ALUMNI_ANALYTICS: `${API_URL}/AdminMaster/GetAlumniAnalytics`,
    DOWNLOAD_TEMPLATE: `${API_URL}/AdminMaster/download-template`,
    UPLOAD_STUDENTS: (uploadedByUserId) => `${API_URL}/AdminMaster/upload?uploadedByUserId=${uploadedByUserId}`,
    MOVE_TO_MASTER: `${API_URL}/AdminMaster/move-to-master`,
    GET_ALL_UPLOADED_ALUMNI_DATA: (uploadedBy, isProcessed) => {
      let url = `${API_URL}/AdminMaster/get-all-uploaded-alumni-data`;
      const params = [];
      if (uploadedBy) params.push(`uploadedBy=${uploadedBy}`);
      if (isProcessed !== undefined && isProcessed !== null) params.push(`isProcessed=${isProcessed}`);
      return params.length > 0 ? `${url}?${params.join('&')}` : url;
    },
    MOVE_SELECTED_TO_MASTER: `${API_URL}/AdminMaster/move-selected-to-master`,
    DELETE_UPLOADED_ALUMNI_DATA: `${API_URL}/AdminMaster/delete-uploaded-alumni-data`,
    
    // Chapter Management
    GET_CHAPTERS: `${API_URL}/AdminMaster/GetChapters`,
    MERGE_CHAPTER: `${API_URL}/AdminMaster/MergeChapter`,
    DELETE_CHAPTER: `${API_URL}/AdminMaster/DeleteChapter`,
    ADMIN_CHAPTER_DROPDOWN_DETAILS: `${API_URL}/AdminMaster/AdminChapterDropdownDetails`,
    FETCH_CHAPTER_MEMBER_ROLES: (chapterId) => `${API_URL}/AdminMaster/FetchChapterMemberRoles/${chapterId}`,
    UPDATE_CHAPTER_MEMBER_ROLE: `${API_URL}/AdminMaster/UpdateChapterMemberRole`,
    INSERT_CHAPTER_ROLE: `${API_URL}/AdminMaster/InsertChapterRole`,
    
    // Event Management
    GET_CHAPTER_EVENTS_ADMIN: `${API_URL}/AdminMaster/GetChapterEventsAdmin`,
    MERGE_CHAPTER_EVENT: `${API_URL}/AdminMaster/MergeChapterEvent`,
    DELETE_CHAPTER_EVENT: `${API_URL}/AdminMaster/DeleteChapterEvent`,
    UPLOAD_EVENT_MEDIA: (eventId, createdBy) => `${API_URL}/AdminMaster/UploadEventMedia?eventId=${eventId}&createdBy=${createdBy}`,
    GET_EVENT_MEDIA: (eventId) => `${API_URL}/AdminMaster/GetEventMedia/${eventId}`,
    DELETE_EVENT_MEDIA: `${API_URL}/AdminMaster/DeleteEventMedia`,
    
    // Screen Rights Management
    GET_STAFF_SCREEN_RIGHTS: (staffId) => `${API_URL}/AdminMaster/GetStaffScreenRights/${staffId}`,
    GET_ALL_SCREENS: `${API_URL}/AdminMaster/GetAllScreens`,
    GET_STAFF_LIST_FOR_SCREEN_RIGHTS: `${API_URL}/AdminMaster/GetStaffListForScreenRights`,
    UPDATE_STAFF_SCREEN_RIGHT: `${API_URL}/AdminMaster/UpdateStaffScreenRight`,
    BULK_UPDATE_STAFF_SCREEN_RIGHTS: `${API_URL}/AdminMaster/BulkUpdateStaffScreenRights`,
    CHECK_SCREEN_ACCESS: (staffId, screenCode) => `${API_URL}/AdminMaster/CheckScreenAccess?staffId=${staffId}&screenCode=${screenCode}`,
  },
  
  // LinkedIn Endpoints
  LINKEDIN: {
    CALLBACK: (code, state) => `${API_URL}/linkedin/callback?code=${code}&state=${state}`,
    SAVE_PROFILE: `${API_URL}/linkedin/save-profile`,
  },
  
  // Student Request Endpoints
  STUDENT_REQUEST: {
    CREATE_REQUEST: `${API_URL}/StudentRequest/create-request`,
    REQUEST_STATUS: (erpStudentId) => `${API_URL}/StudentRequest/request-status/${erpStudentId}`,
    DOWNLOAD_TEMPLATE: `${API_URL}/StudentRequest/download-template`,
    UPLOAD_CURRENT_STUDENTS: (uploadedByUserId) => `${API_URL}/StudentRequest/upload-current-students?uploadedByUserId=${uploadedByUserId}`,
    GET_ALL_UPLOADED_STUDENTS: (isActive) => `${API_URL}/StudentRequest/get-all-uploaded-students${isActive !== undefined ? `?isActive=${isActive}` : ''}`,
    GET_UPLOAD_STATS: `${API_URL}/StudentRequest/upload-stats`,
    DELETE_UPLOADED_STUDENTS: `${API_URL}/StudentRequest/delete-uploaded-students`,
  },
  
  // Auth Endpoints (Admin)
  AUTH_ADMIN: {
    STAFF_LOGIN: `${API_URL}/Auth/staff-login`,
  },

  // Chat Endpoints
  CHAT: {
    BASE: `${API_URL}/Chat`,
    SEND_MESSAGE: `${API_URL}/Chat/send`,
  },

  // WhatsApp Endpoints
  WHATSAPP: {
    SEND_INVITATIONS: `${API_URL}/WhatsApp/send-invitations`,
    GET_MESSAGE_LOGS: `${API_URL}/WhatsApp/message-logs`,
    GET_DATEWISE_LOGS: `${API_URL}/WhatsApp/message-logs-datewise`,
    GET_LOGS_BY_DATE: `${API_URL}/WhatsApp/message-logs-by-date`,
    VALIDATE_TEMPLATE: (templateName) => `${API_URL}/WhatsApp/validate-template/${templateName}`,
  },
  
  // Unknown Alumni Endpoints
  UNKNOWN_ALUMNI: {
    REGISTER: `${API_URL}/UnknownAlumni/register`,
    GET_LIST: (status) => `${API_URL}/UnknownAlumni/list${status ? `?status=${status}` : ''}`,
    GET_BY_ID: (id) => `${API_URL}/UnknownAlumni/${id}`,
    VERIFY: `${API_URL}/UnknownAlumni/verify`,
    GET_MASTER_DATA: `${API_URL}/UnknownAlumni/master-data`,
    GET_INSTITUTES_BY_TYPE: (instTypeId) => `${API_URL}/UnknownAlumni/institutes/${instTypeId}`,
    GET_DISTRICTS_BY_STATE: (stateId) => `${API_URL}/UnknownAlumni/districts/${stateId}`,
    GET_LOCATIONS_BY_DISTRICT: (districtId) => `${API_URL}/UnknownAlumni/locations/${districtId}`,
    GET_STATISTICS: `${API_URL}/UnknownAlumni/statistics`,
    GET_ANALYTICS: `${API_URL}/UnknownAlumni/analytics`,
  },
};

/**
 * Helper function to get full URL for uploads/static files
 */
const getUploadUrl = (path) => {
  if (!path) return null;
  // If path already contains the base URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If path starts with /uploads, use UPLOADS_BASE_URL
  if (path.startsWith('/uploads')) {
    return `${API_BASE_URL}${path}`;
  }
  // If path starts with /, prepend base URL
  if (path.startsWith('/')) {
    return `${API_BASE_URL}${path}`;
  }
  // Otherwise, assume it's a filename in uploads folder
  return `${UPLOADS_BASE_URL}/${path}`;
};

/**
 * Helper function to get full URL for any relative path
 */
const getFullUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${API_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
};

// Export configuration
export default {
  API_BASE_URL,
  API_BASE_PATH,
  API_URL,
  UPLOADS_BASE_URL,
  SIGNALR_HUB_URL,
  API_ENDPOINTS,
  getUploadUrl,
  getFullUrl,
};

// Named exports for convenience
export {
  API_BASE_URL,
  API_BASE_PATH,
  API_URL,
  UPLOADS_BASE_URL,
  SIGNALR_HUB_URL,
  API_ENDPOINTS,
  getUploadUrl,
  getFullUrl,
};

